import { useState, useEffect, useRef } from 'react';

const STORAGE_PREFIX = 'products.v1:';

const readStored = <T>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const writeStored = <T>(key: string, value: T) => {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch {
    /* quota exceeded — ignore */
  }
};

/**
 * useState с автосохранением в localStorage.
 * @param key уникальный ключ хранения (включайте productId если данные зависят от продукта)
 * @param initialValue значение/функция для первого запуска, если в localStorage ничего нет
 */
export function usePersistedState<T>(
  key: string,
  initialValue: T | (() => T),
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    const stored = readStored<T>(key);
    if (stored !== null) return stored;
    return typeof initialValue === 'function' ? (initialValue as () => T)() : initialValue;
  });

  const keyRef = useRef(key);
  useEffect(() => { keyRef.current = key; }, [key]);

  useEffect(() => {
    writeStored(keyRef.current, state);
  }, [state]);

  return [state, setState];
}

export const clearPersistedState = (key?: string) => {
  try {
    if (key) {
      localStorage.removeItem(STORAGE_PREFIX + key);
      return;
    }
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
};
